import { lazy, Suspense, useEffect } from 'react';
import { Route, Routes } from 'react-router-dom';
import PublicLayout from './routes/PublicLayout';
import Index from './routes/Index';
import Teams from './routes/Teams';
import TeamHome from './routes/TeamHome';
import EventDetails from './routes/EventDetails';
import Login from './routes/Login';
import Signup from './routes/Signup';
import AcceptInvite from './routes/AcceptInvite';
import MemberHome from './routes/MemberHome';
import UserProfile from './routes/UserProfile';
import AdminLayout from './routes/admin/AdminLayout';
import AdminHome from './routes/admin/AdminHome';
import AdminTeams from './routes/admin/Teams';
import TeamDetail from './routes/admin/TeamDetail';
import TeamDetails from './routes/admin/TeamDetails';
import Roster from './routes/admin/Roster';
import PlayerDetails from './routes/admin/PlayerDetails';
import Opponents from './routes/admin/Opponents';
import Games from './routes/admin/Games';
import Schedule from './routes/admin/Schedule';
import Fans from './routes/admin/Fans';
import Members from './routes/admin/Members';
import Calendar from './routes/admin/Calendar';
import AdminEventDetails from './routes/admin/EventDetails';
import Announcements from './routes/admin/Announcements';
import Stats from './routes/admin/Stats';
import Clips from './routes/admin/Clips';

// Lazy-load anything that pulls in hls.js / amazon-ivs-player / wasm. The
// public landing page shouldn't pay that cost up-front.
const importGame = () => import('./routes/Game');
const importGoLive = () => import('./routes/admin/GoLive');
const importScorekeeper = () => import('./routes/admin/Scorekeeper');
const importLineup = () => import('./routes/admin/Lineup');
const importReview = () => import('./routes/admin/Review');

const GamePage = lazy(importGame);
const GoLive = lazy(importGoLive);
const Scorekeeper = lazy(importScorekeeper);
const Lineup = lazy(importLineup);
const Review = lazy(importReview);

/** Suspense fallback that preserves layout (no centered "Loading..." that
 *  shoves the page around). The chunks for these routes are tiny — most
 *  loads are <100ms — so an empty placeholder reads as a brief paint
 *  rather than a loading state. */
const RouteFallback = () => <div className="min-h-[60vh]" aria-busy="true" />;

/** Warm the lazy chunks during the first idle period after mount so the
 *  *first* navigation into any of them is instant. They sit on a CDN with
 *  long cache headers, so the cost is one-time. */
function useIdlePreload() {
  useEffect(() => {
    const preload = () => {
      void importGame();
      void importGoLive();
      void importScorekeeper();
      void importLineup();
      void importReview();
    };
    type IC = (cb: () => void, opts?: { timeout: number }) => number;
    const ric = (window as unknown as { requestIdleCallback?: IC }).requestIdleCallback;
    if (ric) {
      const id = ric(preload, { timeout: 2000 });
      return () => {
        const cic = (window as unknown as { cancelIdleCallback?: (id: number) => void })
          .cancelIdleCallback;
        if (cic) cic(id);
      };
    }
    const id = window.setTimeout(preload, 1500);
    return () => window.clearTimeout(id);
  }, []);
}

export default function App() {
  useIdlePreload();
  return (
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      {/* Public site — top navbar layout */}
      <Route element={<PublicLayout />}>
        <Route path="/" element={<Index />} />
        <Route path="/teams" element={<Teams />} />
        <Route path="/teams/:teamId" element={<TeamHome />} />
        <Route path="/teams/:teamId/events/:eventId" element={<EventDetails />} />
        <Route path="/teams/:teamId/games/:gameId" element={<GamePage />} />
        <Route path="/games/:id" element={<GamePage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/invites/accept" element={<AcceptInvite />} />
        <Route path="/me" element={<MemberHome />} />
        <Route path="/me/profile" element={<UserProfile />} />
      </Route>

      {/* Admin — Catalyst SidebarLayout */}
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<AdminHome />} />
        <Route
          path="profile"
          element={<UserProfile backHref="/admin" backLabel="Admin" signInFrom="/admin/profile" />}
        />
        <Route path="teams" element={<AdminTeams />} />
        <Route path="teams/:teamId" element={<TeamDetail />}>
          <Route index element={<Roster />} />
          <Route path="details" element={<TeamDetails />} />
          <Route path="players/:playerId" element={<PlayerDetails />} />
          <Route path="games" element={<Games />} />
          <Route path="schedule" element={<Schedule />} />
          <Route path="calendar" element={<Calendar />} />
          <Route path="events/:eventId" element={<AdminEventDetails />} />
          <Route path="opponents" element={<Opponents />} />
          <Route path="fans" element={<Fans />} />
          <Route path="members" element={<Members />} />
          <Route path="announcements" element={<Announcements />} />
          <Route path="stats" element={<Stats />} />
          <Route path="clips" element={<Clips />} />
        </Route>
        <Route
          path="teams/:teamId/games/:gameId/golive"
          element={<GoLive />}
        />
        <Route
          path="teams/:teamId/games/:gameId/score"
          element={<Scorekeeper />}
        />
        <Route
          path="teams/:teamId/games/:gameId/lineup"
          element={<Lineup />}
        />
        <Route
          path="teams/:teamId/games/:gameId/review"
          element={<Review />}
        />
      </Route>
    </Routes>
    </Suspense>
  );
}
